import type { PropsWithChildren, ReactNode } from "react";
import { useEffect, useRef } from "react";

import { image } from "../assets/manifest";
import { Button } from "./Button";

export type ModalVariant = "confirm" | "danger" | "form" | "picker" | "success";

const MODAL_MASCOTS: Record<ModalVariant, string> = {
  confirm: image.modalConfirm,
  danger: image.modalDanger,
  form: image.modalForm,
  picker: image.modalPicker,
  success: image.modalSuccess
};

type ModalProps = PropsWithChildren<{
  title: string;
  onClose: () => void;
  footer?: ReactNode;
  /** Visual tone; also picks a default corner mascot. */
  variant?: ModalVariant;
  /** Override mascot asset, or `false` to hide. */
  mascot?: string | false;
}>;

export function Modal({ title, onClose, footer, children, variant = "confirm", mascot }: ModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef(onClose);
  const mascotSrc = mascot === false ? null : mascot || MODAL_MASCOTS[variant] || MODAL_MASCOTS.confirm;
  const mascotSide = variant === "danger" || variant === "success" ? "right" : "left";

  useEffect(() => { closeRef.current = onClose; }, [onClose]);

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusableSelector = "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])";
    const focusFirst = () => {
      const first = panelRef.current?.querySelector<HTMLElement>(focusableSelector);
      first?.focus();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(focusableSelector)).filter(
        (item) => !item.matches(":disabled")
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const focusTimer = window.setTimeout(focusFirst, 0);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      previousFocus?.focus();
    };
  }, []);

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        ref={panelRef}
        className={`modal-panel modal--${variant}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <span className="modal-grass" aria-hidden="true" />
        {mascotSrc ? (
          <img
            className={`modal-mascot modal-mascot-${mascotSide}`}
            src={mascotSrc}
            alt=""
            aria-hidden="true"
          />
        ) : null}
        <div className="modal-heading">
          <h3>{title}</h3>
          <Button variant="icon" className="icon-pill-close" aria-label="关闭" onClick={onClose} />
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

export function ModalFooter({
  onCancel,
  onSubmit,
  cancelLabel = "取消",
  submitLabel = "保存",
  danger,
  loading,
  cancelDisabled,
  submitDisabled
}: {
  onCancel: () => void;
  onSubmit: () => void;
  cancelLabel?: string;
  submitLabel?: string;
  danger?: boolean;
  loading?: boolean;
  cancelDisabled?: boolean;
  submitDisabled?: boolean;
}) {
  return (
    <>
      <Button variant="secondary" onClick={onCancel} disabled={loading || cancelDisabled}>
        {cancelLabel}
      </Button>
      <Button
        variant={danger ? "danger" : "primary"}
        onClick={onSubmit}
        loading={loading}
        disabled={submitDisabled}
      >
        {submitLabel}
      </Button>
    </>
  );
}

export function ConfirmDialog({
  title,
  detail,
  children,
  danger,
  confirmLabel = "确认",
  cancelLabel = "取消",
  confirmDisabled = false,
  loading = false,
  closeOnConfirm = true,
  mascot,
  variant,
  onConfirm,
  onClose
}: {
  title: string;
  detail?: string;
  children?: ReactNode;
  danger?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmDisabled?: boolean;
  loading?: boolean;
  closeOnConfirm?: boolean;
  mascot?: string | false;
  variant?: ModalVariant;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}) {
  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  return (
    <Modal
      title={title}
      onClose={handleClose}
      variant={variant ?? (danger ? "danger" : "confirm")}
      mascot={mascot}
      footer={
        <ModalFooter
          onCancel={handleClose}
          cancelLabel={cancelLabel}
          submitLabel={confirmLabel}
          danger={danger}
          loading={loading}
          submitDisabled={confirmDisabled}
          onSubmit={() => {
            void Promise.resolve(onConfirm())
              .then(() => {
                if (closeOnConfirm) onClose();
              })
              .catch(() => {
                // Callers surface errors (toast); keep the dialog open on failure.
              });
          }}
        />
      }
    >
      {children ?? (detail ? <p className="modal-detail">{detail}</p> : null)}
    </Modal>
  );
}
